import { useEffect, useRef } from 'react';
import {
  CLIENT_TIMEOUT_MS,
  pingSelfReferralFiles,
  POLL_INTERVAL_MS,
} from 'API/SelfServiceReferrals/pingFileExisting';
import { useDispatchApp } from 'Hooks';
import { useAppSelector } from 'Hooks/useAppSelector';
import {
  ISelfReferralPingItem,
  SelfReferralPingStatus,
} from 'Interfaces/SelfServiceReferrals';
import {
  IReferralTrackingItem,
  ReferralTrackingStatus,
} from 'Interfaces/SelfReferralTracking';
import { updateTrackingStatus } from 'Store/SelfReferralTrackingSlice';

const mapPingStatus = (
  status: SelfReferralPingStatus,
): ReferralTrackingStatus | null => {
  switch (status) {
    case SelfReferralPingStatus.Success:
      return 'ready';
    case SelfReferralPingStatus.Pending:
    case SelfReferralPingStatus.NotFound:
      return null;
    case SelfReferralPingStatus.Timeout:
      return 'failed';
    default:
      return null;
  }
};

const findPingItem = (
  trackingItem: IReferralTrackingItem,
  results: readonly ISelfReferralPingItem[],
  pendingCount: number,
): ISelfReferralPingItem | undefined => {
  const byTicker = results.find(
    (item) => item.ticker === trackingItem.requestId,
  );
  if (byTicker) return byTicker;
  if (pendingCount === 1 && results.length === 1) return results[0];
  return undefined;
};

export const useSelfReferralStatusPolling = (): void => {
  const dispatch = useDispatchApp();
  const pendingItems = useAppSelector((state) =>
    state.selfReferralTracking.items.filter(
      (item) => item.status === 'pending' && !item.isDismissed,
    ),
  );
  const pendingItemsRef = useRef(pendingItems);
  pendingItemsRef.current = pendingItems;

  useEffect(() => {
    if (pendingItems.length === 0) return undefined;

    const pollPendingItems = async () => {
      if (document.hidden) return;

      const items = pendingItemsRef.current;
      const now = Date.now();
      const activePendingItems: IReferralTrackingItem[] = [];

      for (const item of items) {
        if (now - item.createdAt >= CLIENT_TIMEOUT_MS) {
          dispatch(
            updateTrackingStatus({
              trackingId: item.trackingId,
              status: 'failed',
            }),
          );
          continue;
        }

        activePendingItems.push(item);
      }

      if (activePendingItems.length === 0) return;

      try {
        const results = await pingSelfReferralFiles(
          activePendingItems.map((item) => item.requestId),
        );

        for (const trackingItem of activePendingItems) {
          const pingItem = findPingItem(
            trackingItem,
            results,
            activePendingItems.length,
          );
          if (!pingItem) continue;

          const mappedStatus = mapPingStatus(pingItem.status);
          if (mappedStatus === 'ready') {
            dispatch(
              updateTrackingStatus({
                trackingId: trackingItem.trackingId,
                status: 'ready',
                fileContentBase64: pingItem.fileContentBase64 ?? undefined,
              }),
            );
          } else if (mappedStatus === 'failed') {
            dispatch(
              updateTrackingStatus({
                trackingId: trackingItem.trackingId,
                status: 'failed',
              }),
            );
          }
        }
      } catch {
        // keep pending; will retry on next interval
      }
    };

    void pollPendingItems();
    const intervalId = window.setInterval(pollPendingItems, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, pendingItems.length]);
};
